import { formatDistanceToNow, parseISO } from 'date-fns'
import { Link } from 'react-router-dom'
import { ClassifierLibraryRowItemFragment } from '../../../api/client'
import Badge from '../../../components/badges/Badge'
import ModalityPill from '../../../components/badges/ModalityPill'
import ValencePill from '../../../components/badges/ValencePill'
import VersionCell from '../../../components/classifier/VersionCell'

export const classifierEditPath = (id: number) => `/admin/classifiers/${id}/edit`

const CELL = 'px-3 py-3 align-top'

/**
 * updatedAt comes through the generated client as a `DateTime` scalar — `any` at the
 * type level — so the value is checked rather than trusted. A row with an unreadable
 * timestamp still renders; it just dashes out that one cell.
 */
function UpdatedAt({ value }: { value: unknown }) {
    if (typeof value !== 'string') return <span className="text-ink-400">—</span>
    try {
        return <>{formatDistanceToNow(parseISO(value), { addSuffix: true })}</>
    } catch {
        return <span className="text-ink-400">—</span>
    }
}

/** Deployment reach, counted from ModerationBoundary rather than the classifier itself. */
function Coverage({ profiles, teams }: { profiles: number; teams: number }) {
    if (profiles === 0) return <span className="text-ink-400">Not applied</span>

    return (
        <>
            {profiles} {profiles === 1 ? 'profile' : 'profiles'}
            <span className="text-ink-400">
                {' '}
                · {teams} {teams === 1 ? 'team' : 'teams'}
            </span>
        </>
    )
}

/**
 * One classifier in the library table. The name is a real link stretched over the whole
 * row, so Cmd/Ctrl-click opens a background tab and leaves this page alone — an onClick
 * navigate on the row can't do that. — Claude
 */
export default function ClassifierLibraryRow({
    classifier
}: {
    classifier: ClassifierLibraryRowItemFragment
}) {
    return (
        <tr className="relative hover:bg-ink-50">
            <td className={CELL}>
                <div className="flex items-center gap-1.5">
                    <Link
                        to={classifierEditPath(classifier.id)}
                        className="font-semibold text-ink-900 after:absolute after:inset-0 after:content-['']"
                    >
                        {classifier.displayName}
                    </Link>
                    {classifier.archived && (
                        <Badge tone="neutral" size="tiny" weight="bold" caps>
                            Archived
                        </Badge>
                    )}
                </div>
                <div className="mt-0.5 max-w-md text-xs text-ink-500">
                    {classifier.shortDescription}
                </div>
            </td>
            <td className={CELL}>
                <ModalityPill modality={classifier.modality} />
            </td>
            <td className={CELL}>
                <ValencePill valence={classifier.valence} />
            </td>
            <td className={`${CELL} font-mono text-xs text-ink-700`}>{classifier.model}</td>
            <td className={`${CELL} text-ink-700`}>
                <Coverage profiles={classifier.coveredProfiles} teams={classifier.coveredTeams} />
            </td>
            <td className={CELL}>
                <VersionCell live={classifier.liveVersion} draft={classifier.draftVersion} />
            </td>
            <td className={`${CELL} text-ink-500`}>
                <UpdatedAt value={classifier.updatedAt} />
            </td>
        </tr>
    )
}
